import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MaterialIcon } from '@dsny/dsny-component-library'
import Tooltip from 'components/Tooltip'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { MoreActionIcon } from './ReleaseMoreActionsDropdown.styles'
import ReleaseActionsMenuContent, {
  menuContentClassName
} from './ReleaseActionsMenuContent'

const ReleaseMoreActionsDropdown = ({
  listReleases,
  onOpenChange,
  source = 'releases_view_release_list_more_actions',
  variant = 'list'
}) => {
  const { t } = useTranslation()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const isMobile = useMobileMediaQuery()
  const theme = useTheme()
  const isGrid = variant === 'grid'
  const gridTriggerSize = isMobile ? 12 : 18
  const openedWithPointerRef = useRef(false)
  const triggerRef = useRef(null)

  const handleCloseAutoFocus = (event) => {
    if (!openedWithPointerRef.current) return

    event.preventDefault()
    triggerRef.current?.blur()
    openedWithPointerRef.current = false
  }

  const triggerIcon = (
    <div className="relative items-center justify-around">
      <MoreActionIcon
        $isGrid={isGrid}
        $responsiveBreakpointsV2={responsiveBreakpointsV2}>
        <MaterialIcon
          color={theme.colors.neutralW100}
          name="MoreVertical"
        />
      </MoreActionIcon>
    </div>
  )

  return (
    <DropdownMenu.Root onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger
        ref={triggerRef}
        aria-label={t('general.more-actions')}
        data-cy={
          isGrid ? 'release-grid-more-actions' : 'release-list-more-actions'
        }
        onClick={(event) => event.stopPropagation()}
        onKeyDown={() => {
          openedWithPointerRef.current = false
        }}
        onPointerDown={(event) => {
          event.stopPropagation()
          openedWithPointerRef.current = true
        }}
        style={{
          height: isGrid ? gridTriggerSize : undefined,
          justifySelf: 'center',
          maxHeight: 30,
          padding: isGrid ? 0 : undefined,
          position: 'relative',
          width: isGrid ? gridTriggerSize : undefined
        }}
        type="button">
        {isGrid || isMobile ? (
          triggerIcon
        ) : (
          <Tooltip
            containerStyles={{
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              whiteSpace: 'normal',
              width: 90
            }}
            hasArrow={false}
            placement="top"
            text={t('general.more-actions')}
            wrapperStyles={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              width: isMobile ? '100%' : 'auto'
            }}>
            {triggerIcon}
          </Tooltip>
        )}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          avoidCollisions
          className={menuContentClassName}
          collisionPadding={20}
          data-cy="release-more-actions-menu"
          onCloseAutoFocus={handleCloseAutoFocus}
          side="bottom"
          sideOffset={5}
          style={{
            background: theme.colors.neutralW20,
            color: theme.colors.neutralW100
          }}>
          <ReleaseActionsMenuContent
            Menu={DropdownMenu}
            release={listReleases}
            source={source}
          />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

ReleaseMoreActionsDropdown.propTypes = {
  listReleases: PropTypes.object.isRequired,
  onOpenChange: PropTypes.func,
  source: PropTypes.string,
  variant: PropTypes.oneOf(['grid', 'list'])
}

export default ReleaseMoreActionsDropdown
